/**
 * VAT number <-> company number. In the EU the VAT number is the country's
 * prefix followed by the national company number (Belgium: BE + the 10-digit
 * BCE number), so either one gives the other.
 */

// VAT prefixes that differ from the ISO country code.
const VAT_PREFIX = { GR: 'EL' };
const COUNTRY_OF_PREFIX = { EL: 'GR' };

const compact = (value) => (value || '').toUpperCase().replace(/[\s.\-/]/g, '');

export function vatPrefix(country) {
  const code = (country || '').toUpperCase();
  return VAT_PREFIX[code] || code;
}

/** The country a VAT number names by its prefix, or '' when it has none. */
export function countryOfVat(vat) {
  const prefix = compact(vat).slice(0, 2);
  if (!/^[A-Z]{2}$/.test(prefix)) return '';
  return COUNTRY_OF_PREFIX[prefix] || prefix;
}

/** Belgian BCE number as written on official documents: 0123.456.789. */
function formatBce(digits) {
  const d = digits.padStart(10, '0').slice(-10);
  return `${d.slice(0, 4)}.${d.slice(4, 7)}.${d.slice(7)}`;
}

/** The company number inside a VAT number (its prefix removed). */
export function companyNumberFromVat(vat, country) {
  const value = compact(vat);
  const own = countryOfVat(value);
  const national = own ? value.slice(2) : value;
  if (!national) return '';
  const target = (own || country || '').toUpperCase();
  if (target === 'BE') {
    const digits = national.replace(/\D/g, '');
    return digits ? formatBce(digits) : '';
  }
  return national;
}

/** The VAT number for a company number in that country (prefix added). */
export function vatFromCompanyNumber(companyNumber, country) {
  let value = compact(companyNumber);
  const prefix = vatPrefix(country);
  if (prefix && value.startsWith(prefix)) value = value.slice(prefix.length);
  if (!value || !prefix) return '';
  if (country.toUpperCase() === 'BE') {
    const digits = value.replace(/\D/g, '');
    return digits ? `BE${digits.padStart(10, '0').slice(-10)}` : '';
  }
  return `${prefix}${value}`;
}

/**
 * The client's identification after one field changed, the other one kept in
 * step: a VAT number fills the company number (and the country it names), a
 * company number or a new country fills the VAT number.
 */
export function syncIdentification(client, field, value, countries = []) {
  const next = { ...client, [field]: value };
  if (field === 'vat_number') {
    const named = countryOfVat(value);
    if (named && countries.includes(named)) next.country_code = named;
    next.company_number = companyNumberFromVat(value, next.country_code);
  } else if (field === 'company_number') {
    next.vat_number = vatFromCompanyNumber(value, next.country_code || 'BE');
  } else if (field === 'country_code' && next.company_number) {
    next.vat_number = vatFromCompanyNumber(next.company_number, value);
  }
  return next;
}
